/**
 * The staff register.
 *
 * An employee and a user account are **different records**, and this page shows
 * both states because the difference is exactly what somebody comes here to
 * diagnose. `is_active` is the employment; `account_active` is whether they can
 * sign in. Somebody who has left is inactive on both; somebody on leave is
 * active on both; an account suspended for a security review is one and not the
 * other.
 *
 * Creating a person happens under Access, not here — because what is really
 * being created is a login, and pretending otherwise is how an employee record
 * ends up with no way to sign in.
 */

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Mail, Phone, Plus, Users } from 'lucide-react';
import {
  Button,
  Can,
  PageHeader,
  ServerTable,
  StatusChip,
  createFacetsFetcher,
  createServerFetcher,
  formatDate,
  type ServerColumn,
} from '@connect/web-shared';

export interface EmployeeRow {
  public_id: string;
  code: string;
  full_name: string;
  given_name: string;
  surname: string;
  job_title: string | null;
  /** Null for an account that signs in with a username only. */
  email: string | null;
  mobile: string | null;
  department_name: string | null;
  team_name: string | null;
  manager_name: string | null;
  report_count: number;
  is_active: boolean;
  account_active: boolean;
  joined_on: string | null;
  last_login_at: string | null;
  created_at: string;
}

const ENDPOINT = '/hr/employees';

export function EmployeesPage() {
  const navigate = useNavigate();

  const fetchRows = useMemo(() => createServerFetcher<EmployeeRow>(ENDPOINT), []);
  const getFacets = useMemo(() => createFacetsFetcher(ENDPOINT), []);

  const columns = useMemo<ServerColumn<EmployeeRow>[]>(
    () => [
      {
        field: 'code',
        header: 'Code',
        filter: 'text',
        width: 110,
        render: (row) => <span className="font-mono text-xs text-muted">{row.code}</span>,
      },
      {
        field: 'full_name',
        header: 'Name',
        icon: <Users className="size-3" />,
        filter: 'text',
        minWidth: 200,
        render: (row) => (
          <span className="flex flex-col">
            <span className="truncate font-medium">{row.full_name}</span>
            {row.job_title ? (
              <span className="truncate text-2xs text-faint">{row.job_title}</span>
            ) : null}
          </span>
        ),
      },
      {
        field: 'email',
        header: 'Email',
        icon: <Mail className="size-3" />,
        // Exact match only on the server: a list total over an inexact filter
        // would reconstruct an address a character at a time.
        filter: 'text',
        minWidth: 210,
      },
      {
        field: 'department_name',
        header: 'Department',
        icon: <Building2 className="size-3" />,
        filter: 'select',
        minWidth: 160,
      },
      { field: 'team_name', header: 'Team', filter: 'select', width: 150 },
      { field: 'manager_name', header: 'Reports to', filter: 'select', minWidth: 160 },
      {
        field: 'report_count',
        header: 'Reports',
        numeric: true,
        width: 95,
        sortable: false,
      },
      {
        field: 'is_active',
        header: 'Employed',
        filter: 'boolean',
        align: 'center',
        width: 110,
        render: (row) =>
          row.is_active ? (
            <StatusChip value="active" tones={{ active: 'success' }} />
          ) : (
            <StatusChip value="left" tones={{ left: 'neutral' }} />
          ),
      },
      {
        field: 'account_active',
        header: 'Can sign in',
        filter: 'boolean',
        align: 'center',
        width: 120,
        sortable: false,
        render: (row) =>
          row.account_active ? (
            <StatusChip value="yes" tones={{ yes: 'success' }} />
          ) : (
            // Worth the warning tone: an employed person who cannot sign in is
            // usually a mistake somebody is about to report as a bug.
            <StatusChip value="no" tones={{ no: row.is_active ? 'warning' : 'neutral' }} />
          ),
      },
      {
        field: 'mobile',
        header: 'Mobile',
        icon: <Phone className="size-3" />,
        filter: 'text',
        width: 150,
        hiddenByDefault: true,
      },
      {
        field: 'last_login_at',
        header: 'Last seen',
        filter: 'date',
        width: 140,
        format: (value) => (value ? formatDate(value as string) : 'Never'),
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      <PageHeader
        title="Employees"
        description="Who works here, where they sit, and who they report to."
        actions={
          <Can permission="iam.user.create">
            <Button size="sm" onClick={() => navigate('/access/users?new=1')}>
              <Plus className="size-3.5" />
              Add a person
            </Button>
          </Can>
        }
      />

      <ServerTable<EmployeeRow>
        columns={columns}
        fetchRows={fetchRows}
        getFacets={getFacets}
        rowKey={(row) => row.public_id}
        queryKey={['hr', 'employees']}
        stateKey="hr.employees"
        searchPlaceholder="Search by name, code or job title"
        rowDetailFields={(row) => [
          { label: 'Email', value: row.email },
          { label: 'Mobile', value: row.mobile },
          { label: 'Job title', value: row.job_title },
          { label: 'Department', value: row.department_name },
          { label: 'Team', value: row.team_name },
          { label: 'Reports to', value: row.manager_name },
          { label: 'Joined', value: formatDate(row.joined_on) },
          {
            label: 'Last signed in',
            value: row.last_login_at ? formatDate(row.last_login_at) : 'Never',
          },
        ]}
        emptyTitle="Nobody here yet"
        emptyDescription="An employee record is created alongside a login, under Access."
      />
    </div>
  );
}
